import * as React from 'react';
import { ShareAltOutlined } from '@ant-design/icons';
import type { Theme } from '@emotion/react';
import { css, useTheme } from '@emotion/react';
import {
  DownAlt,
  Draft,
  Hr,
  IconButton,
  Inline,
  Stack,
  Text,
  useBreakpoint,
  useElementSize,
} from '@resi-media/resi-ui';
import { match } from 'ts-pattern';
import type { ShareModalState } from '@sites/components/ShareModal';
import { ShareModal } from '@sites/components/ShareModal';
import { Skeleton } from '@sites/components/Skeleton';
import { GOOGLE_EVENTS } from '@sites/constants/google-events';
import { LIVE_VIDEO_STATUS } from '@sites/constants/live-player';
import { useLivePlayer } from '@sites/contexts/livePlayerContext';
import { useQueryParams } from '@sites/contexts/queryContext';
import { usePrefix } from '@studio/hooks/use-prefix';

const LiveVideoDetails = () => {
  const utils = Draft.useUtils<Date>();
  const { commonT } = usePrefix('components:', 'breadcrumbs');
  const { setModal, setModalState } = Draft.ModalContext.useModal<ShareModalState>();
  const mediaQuery = useBreakpoint();
  const resiTheme = useTheme();
  const { embedShareUrl } = useQueryParams();
  const [expandDescription, setExpandDescription] = React.useState(false);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const accordionRef = React.useRef<HTMLButtonElement>(null);

  const {
    error,
    isLoading,
    isNotFound,
    liveVideoStatus,
    nextLiveEvent,
    showLiveCountdown,
    videoDescription,
    videoName,
  } = useLivePlayer();

  const { height } = useElementSize({
    box: 'border-box',
    ignoreDimensions: ['top', 'left', 'right', 'bottom'],
    ref: contentRef,
  });

  const { accordionMaxHeight, accordionPadding, showAccordion } = React.useMemo(() => {
    const rootElement = document.documentElement;
    const maxHeightByScreen = match(mediaQuery)
      .with({ xl: true }, () => resiTheme.spacing.x128)
      .otherwise(() => resiTheme.spacing.x80);
    const documentFontSize = parseFloat(window.getComputedStyle(rootElement).fontSize);
    const paddingNumber = parseFloat(resiTheme.spacing.x12) * documentFontSize;
    const maxHeight = parseFloat(maxHeightByScreen) * documentFontSize;
    const heightLimit = maxHeight - paddingNumber - 1;
    const showAccordion = !isLoading && height > heightLimit;

    return {
      accordionMaxHeight: maxHeightByScreen,
      accordionPadding: resiTheme.spacing.x12,
      showAccordion,
    };
  }, [height, isLoading, mediaQuery, resiTheme.spacing]);

  return (
    <Stack
      css={(theme) => css`
        min-height: ${isNotFound ? 'auto' : theme.spacing.x32};
        overflow: hidden;
        position: relative;
        padding-bottom: 0;
        transition: ${expandDescription
          ? `all ${theme.transitions.duration.standard}ms ${theme.transitions.easing.sharp};`
          : `all ${theme.transitions.duration.standard}ms ${theme.transitions.easing.easeInOut};`};

        max-height: ${accordionMaxHeight};

        ${showAccordion &&
        expandDescription &&
        css({
          maxHeight: '5000px',
          paddingBottom: accordionPadding,

          '[data-testid="live-video__btn--toggle-description"]': {
            background: 'none',
            height: 'auto',
          },
        })}
      `}
      dataTestId="live-video-details"
      gap="x5"
    >
      <Inline ref={contentRef} alignItems="flex-start" justifyContent="space-between" overflow="hidden" width="scale">
        <Stack gap="xs" overflow="hidden" width="scale">
          <Inline mb="s" width="scale">
            <Stack width="4/5">
              <Skeleton height="2.5rem" isLoading={isLoading} width="min(1600px, 100%)">
                <Text
                  as="h3"
                  css={(theme) =>
                    css({
                      fontFamily: theme.typography.fontFamilyBody,
                      wordBreak: 'break-word',
                    })
                  }
                  dataTestId="live-video-title"
                  title={videoName}
                  variant={mediaQuery.xl ? 'h3' : 'h5'}
                  weightVariant="semiBold"
                >
                  {videoName}
                </Text>
              </Skeleton>
              <Skeleton height="1rem" isLoading={isLoading} width="x20">
                {nextLiveEvent?.startTime && liveVideoStatus !== LIVE_VIDEO_STATUS.ENDED && (
                  <Text
                    colorVariant="body"
                    css={css`
                      white-space: normal;
                      word-break: break-word;
                      overflow: hidden;
                      // Need the x padding here for characters like J at the beginning of the line
                      padding: 0 2px;
                    `}
                    dataTestId="live-video-startTime-text"
                    title={utils.format(utils.parseISO(nextLiveEvent.startTime), 'normalDateWithWeekday')}
                    variant="body2"
                  >
                    {utils.format(utils.parseISO(nextLiveEvent.startTime), 'normalDateWithWeekday')}
                  </Text>
                )}
              </Skeleton>
            </Stack>
            {(liveVideoStatus === LIVE_VIDEO_STATUS.STARTED ||
              (liveVideoStatus === LIVE_VIDEO_STATUS.NEVER_STARTED && showLiveCountdown)) &&
              !error?.info?.length && (
                <Stack alignItems="flex-end" gap="unset" width="1/5">
                  <IconButton
                    colorVariant="neutral"
                    dataTestId="live-video-share-button"
                    label={commonT('share')}
                    onClick={() => {
                      gtag('event', GOOGLE_EVENTS.SHARE_BUTTON_CLICK, { media_asset_name: videoName });
                      setModalState({ videoTitle: videoName, shareUrl: embedShareUrl });
                      setModal(() => <ShareModal />, {
                        dataTestId: 'share-modal',
                        minHVariant: 's',
                        customCss: {
                          container: (theme: Theme) => css`
                            background-color: ${theme.palette.surface.canvas};
                          `,
                        },
                      });
                    }}
                    sizeVariant="m"
                  >
                    <ShareAltOutlined />
                  </IconButton>
                  <Text variant="body3" weightVariant="semiBold">
                    {commonT('share')}
                  </Text>
                </Stack>
              )}
          </Inline>
          <Skeleton count={3} gap="xs" height="1rem" isLoading={isLoading} mt="s" width="min(1000px, 80%)">
            <Text variant="body2">{videoDescription}</Text>
          </Skeleton>
        </Stack>
      </Inline>
      {showAccordion && (
        <Draft.ButtonBase
          ref={accordionRef}
          aria-label={commonT(expandDescription ? 'viewLess' : 'viewMore')}
          css={(theme) => css`
            cursor: pointer;
            position: absolute;
            bottom: 0;
            height: 40%;
            width: 100%;
            background: linear-gradient(
              ${theme.palette.contrastInverse}00 0%,
              ${theme.palette.contrastInverse}60 20%,
              ${theme.palette.contrastInverse} 90%
            );
            display: flex;
            flex-flow: row nowrap;
            align-items: flex-end;
            justify-content: center;

            :hover {
              span {
                text-decoration: underline;
              }
            }
          `}
          dataTestId="live-video__btn--toggle-description"
          onClick={() => setExpandDescription(!expandDescription)}
          role="button"
          title={commonT(expandDescription ? 'viewLess' : 'viewMore')}
        >
          <Inline alignItems="center" width="scale">
            <Hr flexShrink={1} width="scale" />
            <Text
              as="span"
              colorVariant="body"
              css={css({
                flexShrink: 0,
              })}
              dataTestId="view-more-button"
              variant="body3"
              weightVariant="semiBold"
            >
              <Inline alignItems="center" gap="xs" width="scale">
                <DownAlt
                  css={(theme) => css`
                    font-size: ${theme.typography.pxToRem(12)};
                    transition: transform ${theme.transitions.defaults.standard};
                    ${expandDescription &&
                    `transform: rotate(180deg);
    transform-origin: center;`}
                  `}
                />

                {commonT(expandDescription ? 'viewLess' : 'viewMore')}
              </Inline>
            </Text>
            <Hr flexShrink={1} width="scale" />
          </Inline>
        </Draft.ButtonBase>
      )}
    </Stack>
  );
};

LiveVideoDetails.displayName = 'LiveVideoDetails';

export { LiveVideoDetails };
